import React, { useState, useEffect } from 'react';
import { CenteredModal } from '../../../UI/Modal/CenteredModal';
import { Input } from '../../../UI/V3/Forms/Input/Input';
import Button from '../../../UI/V3/Button/Button';
import PhoneField3 from 'components/UI/PhoneField3';
import { validatePhoneNumber } from '@spike/validations';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Organization } from 'model/organization';
import { ContactType, getContactTypeDisplayName } from 'model/contact';
import { Select } from 'components/UI/V3/Forms/Select/Select';
import styles from './AddContactModal.module.scss';

const contactTypes = [
    { value: ContactType.FirstEmergency, label: 'First Emergency Contact' },
    { value: ContactType.SecondEmergency, label: 'Second Emergency Contact' },
    { value: ContactType.Financial, label: 'Financial Contact' },
    { value: ContactType.Security, label: 'Security Contact' },
    { value: ContactType.Support, label: 'Support Contact' },
    { value: ContactType.Technical, label: 'Technical Contact' }
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface AddContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    contactType: ContactType;
    contactId?: number;
    onSave?: (contactData: ContactFormData, contactId?: number) => void;
    existingContactTypes?: ContactType[];
}

interface ContactFormData {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    contactType: ContactType | string;
}

export const AddContactModal: React.FC<AddContactModalProps> = ({
    isOpen,
    onClose,
    contactType,
    contactId,
    onSave,
    existingContactTypes = []
}) => {
    const organizations = useSelector((state: RootState) => state.organizations.organizations);
    const contacts = useSelector((state: RootState) => state.contacts.contacts);

    // Get parent organization (where parentOrganizationId is null)
    const parentOrganization = organizations.find(
        (org: Organization) => org.parentOrganizationId === null || org.parentOrganizationId === undefined
    );
    const organizationCountry = parentOrganization?.country || 'US';

    const [formData, setFormData] = useState<ContactFormData>({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        contactType: contactType
    });

    const [errors, setErrors] = useState<Partial<ContactFormData>>({});

    // Pre-populate form when editing
    useEffect(() => {
        if (contactId && isOpen) {
            const contact = contacts.find(c => c.id === contactId);
            if (contact) {
                setFormData({
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                    phoneNumber: contact.phone,
                    email: contact.email,
                    contactType: contact.contactType
                });
            }
        } else if (isOpen) {
            // Set initial contactType from props when adding new contact
            setFormData(prev => ({
                ...prev,
                contactType: contactType
            }));
        }
    }, [contactId, isOpen, contacts, contactType]);

    const handleInputChange = (field: keyof ContactFormData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Validate phone field as user types
        if (field === 'phoneNumber') {
            validatePhoneField(value);
            return; // Don't clear error - let validation handle it
        }

        // Clear error when user starts typing (except for phone)
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const validatePhoneField = (phoneValue: string) => {
        let phoneError: string | undefined = undefined;

        // Validate format if value is provided
        if (phoneValue && phoneValue.trim()) {
            const validationErrors = validatePhoneNumber(phoneValue, organizationCountry, 'phoneNumber');
            if (validationErrors.length > 0) {
                phoneError = validationErrors[0].errorMessage;
            }
        }

        setErrors(prev => ({
            ...prev,
            phoneNumber: phoneError
        }));
    };

    const handlePhoneBlur = () => {
        validatePhoneField(formData.phoneNumber);
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<ContactFormData> = {};

        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (formData.firstName.length > 50) newErrors.firstName = 'First name must not exceed 50 characters';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (formData.lastName.length > 50) newErrors.lastName = 'Last name must not exceed 50 characters';

        // When editing, contactType dropdown is shown and must be selected
        if (contactId && !formData.contactType?.trim()) {
            newErrors.contactType = 'Contact type is required';
        }

        // Email validation (must be valid if provided)
        if (formData.email && formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Phone validation (must be valid if provided)
        if (formData.phoneNumber && formData.phoneNumber.trim()) {
            const phoneErrors = validatePhoneNumber(formData.phoneNumber, organizationCountry, 'phoneNumber');
            if (phoneErrors.length > 0) {
                newErrors.phoneNumber = phoneErrors[0].errorMessage;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (validateForm()) {
            onSave?.(formData, contactId);
            handleClose();
        }
    };

    const handleCancel = () => {
        handleClose();
    };

    const handleClose = () => {
        // Reset form when closing
        setFormData({
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: '',
            contactType: ''
        });
        setErrors({});
        onClose();
    };

    const getContactOptions = () => {
        // Get the current contact being edited
        const currentContact = contacts.find(c => c.id === contactId);
        // Filter out contact types that are already used by other contacts
        return contactTypes.filter(type => {
            // If this is the current contact's type, allow it
            if (currentContact?.contactType === type.value) {
                return true;
            }
            // Otherwise, only include if not already used
            return !existingContactTypes.includes(type.value);
        });
    };

    return (
        <CenteredModal
            isOpen={isOpen}
            onClose={handleClose}
            maxWidth="medium"
            className={styles.addContactModal}
        >
            <CenteredModal.Header
                title={contactId ? 'Edit Contact' : getContactTypeDisplayName(contactType as ContactType)}
                showCloseButton={true}
            />

            <CenteredModal.Content className={styles.modalContent}>
                <form
                    className={styles.form}
                    onSubmit={e => e.preventDefault()}
                >
                    {/* Contact Type Dropdown - Only shown when editing */}
                    {contactId && (
                        <div className={styles.formField}>
                            <Select
                                label="Contact type"
                                placeholder="Select contact type"
                                options={getContactOptions()}
                                value={formData.contactType}
                                onChange={value => handleInputChange('contactType', value)}
                                error={errors.contactType}
                                required={true}
                            />
                        </div>
                    )}

                    {/* First Name and Last Name Row */}
                    <div className={styles.formRow}>
                        <div className={styles.formField}>
                            <Input
                                id="firstName"
                                label="First name"
                                value={formData.firstName}
                                onChange={e => handleInputChange('firstName', e.target.value)}
                                placeholder="Enter first name"
                                error={errors.firstName}
                                required={true}
                                maxLength={50}
                            />
                        </div>
                        <div className={styles.formField}>
                            <Input
                                id="lastName"
                                label="Last name"
                                value={formData.lastName}
                                onChange={e => handleInputChange('lastName', e.target.value)}
                                placeholder="Enter last name"
                                error={errors.lastName}
                                required={true}
                                maxLength={50}
                            />
                        </div>
                    </div>

                    {/* Phone Number and Email Row */}
                    <div className={styles.formRow}>
                        <div className={styles.formField}>
                            <PhoneField3
                                id="phoneNumber"
                                name="phoneNumber"
                                label="Phone number"
                                value={formData.phoneNumber}
                                onChange={value => handleInputChange('phoneNumber', value)}
                                onBlur={handlePhoneBlur}
                                placeholder="(___) ___-____"
                                errors={
                                    errors.phoneNumber
                                        ? [{ fieldName: 'phoneNumber', errorMessage: errors.phoneNumber }]
                                        : undefined
                                }
                            />
                        </div>
                        <div className={styles.formField}>
                            <Input
                                id="email"
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={e => handleInputChange('email', e.target.value)}
                                placeholder="Enter email address"
                                error={errors.email}
                            />
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className={styles.formActions}>
                        <Button
                            label="Cancel"
                            variant="outlined"
                            size="large"
                            onClick={handleCancel}
                        />
                        <Button
                            label="Save"
                            variant="black"
                            size="large"
                            onClick={handleSave}
                        />
                    </div>
                </form>
            </CenteredModal.Content>
        </CenteredModal>
    );
};

export default AddContactModal;
